// components/Outreach/CampaignsList.js — С КЛИЕНТСКОЙ ПАГИНАЦИЕЙ
import React, { useState, useEffect } from "react";
import { getCookie } from "../../services/cookie";
import { HiChevronLeft, HiChevronRight, HiPencil, HiDuplicate } from "react-icons/hi";
import { CreateCampaign } from "./CreateCampaign";

const API_BASE = "https://flautreach.com/flautreach/api";
const ITEMS_PER_PAGE = 10; // Показывать по 5 кампаний на странице

const PLATFORM_META = {
  EMAIL: { icon: "📧", label: "Email" },
  FB_MESSENGER: { icon: "💬", label: "FB Messenger" },
  CONTACT_FORM: { icon: "📝", label: "Contact Form" },
};

export function CampaignsList({ onRefresh, onCreateIntentions, add }) {
  const token = () => getCookie("authToken");
  const getHeaders = () => {
    const t = token();
    return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  };

  const [allCampaigns, setAllCampaigns] = useState([]); // Все кампании
  const [campaigns, setCampaigns] = useState([]); // Кампании на текущей странице
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [duplicatingCampaign, setDuplicatingCampaign] = useState(null);

  // Загружаем ВСЕ кампании один раз
  useEffect(() => {
    loadAllCampaigns();
  }, []);

  // При изменении страницы - режем массив
  useEffect(() => {
    if (allCampaigns.length > 0) {
      const startIndex = (page - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const pageCampaigns = allCampaigns.slice(startIndex, endIndex);
      
      setCampaigns(pageCampaigns);
      setTotalPages(Math.ceil(allCampaigns.length / ITEMS_PER_PAGE));
      
      console.log(`Страница ${page}: показываем ${pageCampaigns.length} из ${allCampaigns.length} кампаний`);
    }
  }, [page, allCampaigns]);

  const loadAllCampaigns = async () => {
    setLoading(true);
    try {
      // Загружаем все кампании без пагинации
      const resp = await fetch(`${API_BASE}/outreach/campaigns/`, { 
        headers: getHeaders() 
      });
      
      if (resp.ok) {
        const data = await resp.json();
        console.log("API response:", data);
        
        // Определяем формат ответа
        let campaignsList = [];
        if (Array.isArray(data)) {
          campaignsList = data;
        } else if (data.results && Array.isArray(data.results)) {
          campaignsList = data.results;
        } else if (data.campaigns && Array.isArray(data.campaigns)) {
          campaignsList = data.campaigns;
        } else {
          console.warn("Неизвестный формат ответа:", data);
        }
        
        console.log(`Загружено ${campaignsList.length} кампаний`);
        setAllCampaigns(campaignsList);
        
        // Сбрасываем на первую страницу при новой загрузке
        setPage(1);
      }
    } catch (err) {
      console.error("Ошибка загрузки кампаний:", err);
      add?.("Ошибка загрузки кампаний", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (camp) => {
    try {
      await fetch(`${API_BASE}/outreach/campaigns/${camp.id}/`, {
        method: "PATCH",
        headers: getHeaders(),
        body: JSON.stringify({ is_active: !camp.is_active }),
      });
      add?.(camp.is_active ? "Кампания на паузе" : "Кампания активирована", "success");
      loadAllCampaigns();
      onRefresh?.();
    } catch (err) {
      add?.("Ошибка", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить кампанию?")) return;
    try {
      await fetch(`${API_BASE}/outreach/campaigns/${id}/`, { 
        method: "DELETE", 
        headers: getHeaders() 
      });
      add?.("Кампания удалена", "info");
      
      // Если на странице была 1 кампания и это не первая страница
      if (campaigns.length === 1 && page > 1) {
        setPage(page - 1);
      }
      
      loadAllCampaigns();
      onRefresh?.();
    } catch (err) {
      add?.("Ошибка", "error");
    }
  };

  const handleDuplicate = (camp) => {
    const duplicateData = {
      ...camp,
      name: `${camp.name} (копия)`,
      id: undefined,
      is_active: false,
      intentions_count: 0,
      active_intentions: 0,
    };
    
    setDuplicatingCampaign(duplicateData);
  };

  const handleDeleteAllIntentions = async (campaignId, campaignName) => {
    if (!window.confirm(`Удалить ВСЕ намерения кампании "${campaignName}"?`)) return;
    try {
      const resp = await fetch(`${API_BASE}/outreach/intentions/delete_by_campaign/`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ campaign_id: campaignId }),
      });
      if (resp.ok) {
        const data = await resp.json();
        add?.(`Удалено ${data.deleted || 0} намерений`, "info");
        loadAllCampaigns();
        onRefresh?.();
      } else {
        const err = await resp.json().catch(() => ({}));
        add?.(err.detail || "Ошибка удаления", "error");
      }
    } catch (err) {
      add?.("Ошибка удаления", "error");
    }
  };

  if (editingCampaign || duplicatingCampaign) {
    return (
      <CreateCampaign
        editCampaign={editingCampaign}
        duplicateCampaign={duplicatingCampaign}
        onBack={() => {
          setEditingCampaign(null);
          setDuplicatingCampaign(null);
        }}
        onCreated={() => {
          setEditingCampaign(null);
          setDuplicatingCampaign(null);
          loadAllCampaigns();
          onRefresh?.();
        }}
        add={add}
      />
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <div className="spinner" />
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">📢</div>
        <h3>Нет кампаний</h3>
        <p>Создайте первую аутрич-кампанию</p>
      </div>
    );
  }

  return (
    <div>
      {/* Информация о пагинации */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 16,
        padding: "8px 12px",
        background: "#f8f9ff",
        borderRadius: 8,
        border: "1px solid #e5e7eb",
      }}>
        <span style={{ fontSize: 13, color: "var(--muted)" }}>
          Всего кампаний: <strong>{allCampaigns.length}</strong>
        </span>
        <span style={{ fontSize: 13, color: "var(--muted)" }}>
          Страница {page} из {totalPages} (показано {campaigns.length})
        </span>
      </div>

      <div className="rules-grid" style={{ marginBottom: 20 }}>
        {campaigns.map((camp) => (
          <div key={camp.id} className="rule-card">
            {/* Заголовок */}
            <div className="rule-card-top">
              <div className="rule-intent">
                <p>{camp.name}</p>
                {camp.description && (
                  <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
                    {camp.description}
                  </p>
                )}
              </div>
              <div className={`rule-status-badge ${camp.is_active ? "active" : "paused"}`}>
                <span className={`rule-dot ${camp.is_active ? "active" : "paused"}`} />
                {camp.is_active ? "Активна" : "На паузе"}
              </div>
            </div>

            {/* Платформы */}
            <div className="rule-platforms">
              {(camp.platforms || []).map((p) => {
                const meta = PLATFORM_META[p] || { icon: "📡", label: p };
                return (
                  <span key={p} className="plat-chip">
                    {meta.icon} {meta.label}
                  </span>
                );
              })}
            </div>

            {/* Шаблоны сообщений (развёрнуто) */}
            {expanded === camp.id && camp.message_templates && (
              <div style={{
                marginTop: 16,
                padding: 16,
                background: "#f8f9ff",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
              }}>
                {Object.entries(camp.message_templates).map(([key, tmpl]) => {
                  const meta = PLATFORM_META[key] || { icon: "📡", label: key };
                  return (
                    <div key={key} style={{ marginBottom: 12 }}>
                      <strong style={{ fontSize: 13 }}>{meta.icon} {meta.label}</strong>
                      {tmpl.subject && (
                        <p style={{ fontSize: 12, marginTop: 4, color: "#374151" }}>
                          📌 Тема: <code style={{
                            background: "#e5e7eb",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                          }}>{tmpl.subject}</code>
                        </p>
                      )}
                      <pre style={{
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        marginTop: 4,
                        fontFamily: "inherit",
                        color: "var(--muted)",
                        background: "#fff",
                        padding: "8px 12px",
                        borderRadius: 6,
                        border: "1px solid #e5e7eb",
                      }}>
                        {tmpl.body?.substring(0, 200)}{tmpl.body?.length > 200 ? "..." : ""}
                      </pre>
                    </div>
                  );
                })}

                {/* Промпт диалога */}
                {camp.dialog_prompt && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
                    <strong style={{ fontSize: 13 }}>🤖 Промпт диалога</strong>
                    <pre style={{
                      fontSize: 11,
                      whiteSpace: "pre-wrap",
                      marginTop: 4,
                      fontFamily: "monospace",
                      background: "#1e1e2e",
                      color: "#cdd6f4",
                      padding: "10px 14px",
                      borderRadius: 6,
                      maxHeight: 150,
                      overflow: "auto",
                    }}>
                      {camp.dialog_prompt.substring(0, 300)}{camp.dialog_prompt.length > 300 ? "..." : ""}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Мета-информация */}
            <div className="rule-meta">
              <span>📋 {camp.intentions_count || 0} намерений</span>
              <span>⏳ {camp.active_intentions || 0} активных</span>
              <span className="mentions-count">📅 {new Date(camp.created_at).toLocaleDateString()}</span>
            </div>

            {/* Кнопки действий */}
            <div className="rule-actions" style={{ flexWrap: "wrap", gap: 6 }}>
              <button
                className="btn-toggle paused"
                onClick={() => setExpanded(expanded === camp.id ? null : camp.id)}
              >
                {expanded === camp.id ? "🔼 Скрыть" : "🔽 Тексты"}
              </button>
              <button
                className="btn-toggle paused"
                style={{ color: "#8b5cf6", borderColor: "rgba(139,92,246,.3)" }}
                onClick={() => setEditingCampaign(camp)}
              >
                <HiPencil size={14} style={{ marginRight: 4 }} />
                Ред.
              </button>
              <button
                className="btn-toggle paused"
                style={{ color: "#10b981", borderColor: "rgba(16,185,129,.3)" }}
                onClick={() => handleDuplicate(camp)}
                title="Создать копию кампании"
              >
                <HiDuplicate size={14} style={{ marginRight: 4 }} />
                Копия
              </button>
              <button
                className="btn-toggle paused"
                style={{ color: "var(--blue)", borderColor: "rgba(59,113,254,.3)" }}
                onClick={() => onCreateIntentions?.(camp)}
              >
                ➕ Намерения
              </button>
              <button
                className="btn-delete"
                style={{ color: "#dc2626", borderColor: "#fecaca", fontSize: 11 }}
                onClick={() => handleDeleteAllIntentions(camp.id, camp.name)}
              >
                🗑 Намерения
              </button>
              <button
                className={`btn-toggle ${camp.is_active ? "active" : "paused"}`}
                onClick={() => handleToggleActive(camp)}
              >
                {camp.is_active ? "⏸ Пауза" : "▶ Пуск"}
              </button>
              <button className="btn-delete" onClick={() => handleDelete(camp.id)}>
                🗑
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div style={{
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          gap: 8,
          padding: "12px 0",
        }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{
              minWidth: 36, 
              height: 36, 
              borderRadius: 8,
              border: "1.5px solid var(--border)", 
              background: "#fff",
              cursor: page <= 1 ? "not-allowed" : "pointer",
              opacity: page <= 1 ? 0.4 : 1,
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
            }}
          >
            <HiChevronLeft size={18} />
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => setPage(p)}
              style={{
                minWidth: 36, 
                height: 36, 
                borderRadius: 8, 
                border: "none",
                background: p === page ? "var(--blue)" : "transparent",
                color: p === page ? "#fff" : "var(--dark)",
                fontWeight: p === page ? 600 : 400,
                cursor: "pointer", 
                fontSize: 14,
              }}
            >
              {p}
            </button>
          ))}
          
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            style={{
              minWidth: 36, 
              height: 36, 
              borderRadius: 8,
              border: "1.5px solid var(--border)", 
              background: "#fff",
              cursor: page >= totalPages ? "not-allowed" : "pointer",
              opacity: page >= totalPages ? 0.4 : 1,
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
            }}
          >
            <HiChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
}